import { API_BASE_URL } from './api/client';

/**
 * Browser-fetchable URLs for avatar/logo images stored in S3.
 *
 * Never use the raw `avatarUrl`/`avatarMedium`/`avatarSmall`/`avatarThumbnail` or clinic `logoUrl`
 * fields directly as an `<img src>` — the avatar bucket has Block Public Access enabled and no
 * CloudFront distribution was ever finished being provisioned in front of it, so those stored S3
 * URLs 403 for a direct anonymous fetch (see backend `ImageService.buildUrl`'s note). The backend
 * proxies the actual bytes through an authenticated S3 read instead — these helpers build the URLs
 * for those proxy endpoints.
 */

export type AvatarSize = 'original' | 'medium' | 'small' | 'thumbnail';

/** GET /api/users/{id}/avatar?size=... — undefined (no request) when there's no user id to ask for. */
export function avatarSrc(userId: string | undefined | null, size: AvatarSize = 'medium'): string | undefined {
  if (!userId) return undefined;
  return `${API_BASE_URL}/api/users/${userId}/avatar?size=${size}`;
}

/**
 * GET /api/clinics/{id}/logo — undefined (no request) when there's no clinic id to ask for.
 * `updatedAt`, when available, cache-busts so a fresh upload shows immediately instead of the
 * browser serving what it cached under the same URL for the previous logo.
 */
export function clinicLogoSrc(clinicId: string | undefined | null, updatedAt?: string | null): string | undefined {
  if (!clinicId) return undefined;
  const qs = updatedAt ? `?v=${encodeURIComponent(updatedAt)}` : '';
  return `${API_BASE_URL}/api/clinics/${clinicId}/logo${qs}`;
}
